import { Component, ElementRef, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { Subscription } from 'rxjs';
import { CommonModule } from '@angular/common';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TablerIconsModule } from 'angular-tabler-icons';
import {
  ArchivedConversation,
  ArchivedConversationDetail,
  ConversationArchiveService
} from '@app/core/services/conversation-archive/conversation-archive.service';
import { FilemanagerApiService } from '@services/api/filemanager/filemanager-api.service';
import { FormsModule } from '@angular/forms';
import { CompanyFilterComponent } from '@components/company-filter/company-filter.component';
import { CompanyFilterService } from '@services/company-filter/company-filter.service';

interface ArchiveDay {
  day: string;
  calls: ArchivedConversation[];
}

type Period = 'all' | 'today' | 'week' | 'month';
const PERIOD_DAYS: Record<Exclude<Period, 'all' | 'today'>, number> = { week: 7, month: 30 };

function localDay(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** Translated calls saved as documents, one folder per day, to reread them in the panel. */
@Component({
  selector: 'app-conversations',
  standalone: true,
  imports: [CommonModule, FormsModule, MatProgressSpinnerModule, TranslateModule, TablerIconsModule, CompanyFilterComponent],
  templateUrl: './conversations.component.html',
  styleUrl: './conversations.component.scss'
})
export class ConversationsComponent implements OnInit, OnDestroy {
  private archive = inject(ConversationArchiveService);
  private filemanagerApi = inject(FilemanagerApiService);
  private translate = inject(TranslateService);
  private companyFilter = inject(CompanyFilterService);
  private companySubscription: Subscription | null = null;

  @ViewChild('reader') reader?: ElementRef<HTMLElement>;

  loading = true;
  failed = false;
  detailLoading = false;
  detailFailed = false;
  downloading = false;
  search = '';
  // Filters above the list: period, field technician and, for the superadmin, company (shared "Azienda" menu)
  period: Period = 'all';
  technicianId: number | null = null;
  readonly periods: Period[] = ['all', 'today', 'week', 'month'];
  days: ArchiveDay[] = [];
  selectedDay: string | null = null;
  selected: ArchivedConversation | null = null;
  detail: ArchivedConversationDetail | null = null;
  private all: ArchivedConversation[] = [];
  private detailSubscription: Subscription | null = null;

  ngOnInit(): void {
    this.load();
    this.companySubscription = this.companyFilter.selected$.subscribe(() => {
      // A technician of another company would leave the list empty
      if (this.technicianId !== null && !this.technicianOptions.some(t => t.id === this.technicianId)) {
        this.technicianId = null;
      }
      this.applyFilter();
    });
  }

  get filtersActive(): boolean {
    return !!this.search.trim() || this.period !== 'all' || this.technicianId !== null || this.companyFilter.selected !== null;
  }

  // Field technicians of the conversations of the chosen company, for the technician menu
  get technicianOptions(): { id: number, name: string }[] {
    const seen = new Map<number, string>();
    for (const item of this.all) {
      if (item.technician_id && this.companyFilter.matches(item.company_id) && !seen.has(item.technician_id)) {
        seen.set(item.technician_id, item.technician_name || '—');
      }
    }
    return [...seen.entries()].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name, 'it'));
  }

  setPeriod(period: Period): void {
    this.period = period;
    this.applyFilter();
  }

  setTechnician(id: number | null): void {
    this.technicianId = id;
    this.applyFilter();
  }

  private inPeriod(item: ArchivedConversation): boolean {
    if (this.period === 'all') {
      return true;
    }
    if (this.period === 'today') {
      return item.day === localDay(new Date());
    }
    const from = new Date();
    from.setHours(0, 0, 0, 0);
    from.setDate(from.getDate() - (PERIOD_DAYS[this.period] - 1));
    return item.day >= localDay(from);
  }

  load(): void {
    this.loading = true;
    this.failed = false;
    this.archive.getArchive().subscribe({
      next: conversations => {
        this.all = conversations;
        this.loading = false;
        this.applyFilter();
      },
      error: () => {
        this.loading = false;
        this.failed = true;
      }
    });
  }

  onSearch(value: string): void {
    this.search = value;
    this.applyFilter();
  }

  private applyFilter(): void {
    const query = this.search.trim().toLowerCase();
    const filtered = this.all.filter(item => this.companyFilter.matches(item.company_id) && this.inPeriod(item)
      && (this.technicianId === null || item.technician_id === this.technicianId));
    const visible = query
      ? filtered.filter(item => [item.room_name, item.technician_name, item.operator_name, item.company_name]
        .some(value => (value || '').toLowerCase().includes(query)))
      : filtered;

    const byDay = new Map<string, ArchivedConversation[]>();
    for (const item of visible) {
      byDay.set(item.day, [...(byDay.get(item.day) ?? []), item]);
    }
    this.days = [...byDay.entries()]
      .sort(([a], [b]) => b.localeCompare(a))
      .map(([day, calls]) => ({ day, calls }));

    if (!this.days.some(entry => entry.day === this.selectedDay)) {
      this.selectedDay = this.days[0]?.day ?? null;
    }
    const calls = this.currentCalls;
    if (!this.selected || !calls.some(call => call.uuid === this.selected?.uuid)) {
      this.open(calls[0] ?? null, false);
    }
  }

  get currentCalls(): ArchivedConversation[] {
    return this.days.find(entry => entry.day === this.selectedDay)?.calls ?? [];
  }

  pickDay(day: string): void {
    this.selectedDay = day;
    this.open(this.currentCalls[0] ?? null, false);
  }

  open(call: ArchivedConversation | null, scroll = true): void {
    // With quick clicks the previous answer must not stop the spinner of the conversation now selected
    this.detailSubscription?.unsubscribe();
    this.selected = call;
    this.detail = null;
    this.detailFailed = false;
    this.detailLoading = false;
    if (!call) {
      return;
    }
    this.detailLoading = true;
    this.detailSubscription = this.archive.getConversation(call.uuid).subscribe({
      next: detail => {
        this.detail = detail;
        this.detailLoading = false;
        // On a phone the reader sits under the list: bring it into view
        if (scroll && window.innerWidth < 1200) {
          setTimeout(() => this.reader?.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' }));
        }
      },
      error: () => {
        this.detailLoading = false;
        this.detailFailed = true;
      }
    });
  }

  ngOnDestroy(): void {
    this.detailSubscription?.unsubscribe();
    this.companySubscription?.unsubscribe();
  }

  downloadPdf(): void {
    const call = this.selected;
    if (!call?.document_uuid || this.downloading) {
      return;
    }
    this.downloading = true;
    this.filemanagerApi.getFileBytes(call.document_uuid).subscribe({
      next: (blob: any) => {
        const link = document.createElement('a');
        const url = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
        link.href = url;
        link.download = `${call.day} ${this.time(call.started_at)} - ${call.room_name}.pdf`.replace(/[\\/:*?"<>|]+/g, '-');
        link.click();
        // revoca ritardata: Firefox/Safari interrompono il download se l'URL sparisce subito
        setTimeout(() => URL.revokeObjectURL(url), 1000);
        this.downloading = false;
      },
      error: () => {
        this.downloading = false;
      }
    });
  }

  private get locale(): string {
    const lang = this.translate.currentLang || this.translate.defaultLang || 'it';
    return { it: 'it-IT', en: 'en-GB', es: 'es-ES', de: 'de-DE' }[lang] ?? 'it-IT';
  }

  dayLabel(day: string): string {
    const today = new Date();
    const yesterday = new Date(today.getTime() - 86400000);
    if (day === this.isoDay(today)) {
      return this.translate.instant('conversations.today');
    }
    if (day === this.isoDay(yesterday)) {
      return this.translate.instant('conversations.yesterday');
    }
    return new Intl.DateTimeFormat(this.locale, { day: 'numeric', month: 'long' }).format(this.dayDate(day));
  }

  dayLong(day: string): string {
    return new Intl.DateTimeFormat(this.locale, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
      .format(this.dayDate(day));
  }

  time(value: string | null): string {
    if (!value) {
      return '';
    }
    return new Intl.DateTimeFormat(this.locale, { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Rome' })
      .format(new Date(value));
  }

  timeRange(call: ArchivedConversation): string {
    return `${this.time(call.started_at)} – ${this.time(call.ended_at || call.last_activity_at)}`;
  }

  minutes(call: ArchivedConversation): number {
    const end = new Date(call.ended_at || call.last_activity_at).getTime();
    return Math.max(1, Math.round((end - new Date(call.started_at).getTime()) / 60000));
  }

  /** "en-US / it-IT" → "EN ↔ IT" */
  languages(call: ArchivedConversation): string {
    return call.languages
      .map(pair => pair.split(' / ').map(code => code.split('-')[0].toUpperCase()).join(' ↔ '))
      .join(', ');
  }

  statusKey(call: ArchivedConversation): string {
    if (call.document_uuid) {
      return 'conversations.pdf_ready';
    }
    if (call.status === 'error') {
      return 'conversations.pdf_error';
    }
    if (call.status === 'open') {
      return 'conversations.in_progress';
    }
    return 'conversations.pdf_preparing';
  }

  statusClass(call: ArchivedConversation): string {
    if (call.document_uuid) {
      return 'xr-badge--ok';
    }
    return call.status === 'error' ? 'xr-badge--busy' : 'xr-badge--wait';
  }

  private isoDay(date: Date): string {
    return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Rome' }).format(date);
  }

  private dayDate(day: string): Date {
    const [year, month, date] = day.split('-').map(Number);
    return new Date(year, month - 1, date, 12);
  }
}
